<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { _ } from 'svelte-i18n';
  import ReadOnlyScoreBoard from '../components/ReadOnlyScoreBoard.svelte';
  import { getPublicScoreboard, getPublicScoreboardUpdates } from '../lib/apiService';
  import type { Match } from '../types';

  export let params: { token?: string } = {};

  let match: Match | null = null;
  let loading = true;
  let error: string | null = null;
  let pollingInterval: number | null = null;

  async function loadScoreboard() {
    if (!params.token) {
      error = $_('scoreBoard.invalidLink');
      loading = false;
      return;
    }

    try {
      await getPublicScoreboard(params.token, (result, status) => {
        if (status === 200) {
          match = result;
          loading = false;
          error = null;
        } else if (status === 403) {
          error = $_('scoreBoard.expiredLink');
          loading = false;
        } else if (status === 404) {
          error = $_('scoreBoard.matchNotFound');
          loading = false;
        } else {
          error = $_('scoreBoard.loadError');
          loading = false;
        }
      });
    } catch (err) {
      console.error('Error loading public scoreboard:', err);
      error = $_('scoreBoard.loadError');
      loading = false;
    }
  }

  async function pollForUpdates() {
    if (!params.token || !match) return;

    try {
      await getPublicScoreboardUpdates(params.token, (result, status) => {
        if (status === 200 && match) {
          // Update scores and highlights
          match.score_team = result.score_team;
          match.score_opponent = result.score_opponent;
          match.highlights = result.highlights;
          // Trigger reactivity
          match = match;
        }
      });
    } catch (err) {
      console.error('Error polling for updates:', err);
      // Don't show error to user, just log it
    }
  }

  function startPolling() {
    // Poll every 30 seconds
    pollingInterval = window.setInterval(pollForUpdates, 30000);
  }

  function stopPolling() {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      pollingInterval = null;
    }
  }

  onMount(() => {
    loadScoreboard();
    // Start polling once loaded
    setTimeout(() => {
      if (match && !error) {
        startPolling();
      }
    }, 1000);
  });

  onDestroy(() => {
    stopPolling();
  });
</script>

<div class="share-view">
  <div class="container">
    {#if loading}
      <div class="loading-container">
        <p>{$_('common.loading')}</p>
      </div>
    {:else if error}
      <div class="error-container">
        <article>
          <header>
            <h3>{$_('scoreBoard.shareViewTitle')}</h3>
          </header>
          <p class="error-message">{error}</p>
          <footer>
            <a href="/account" class="button">{$_('common.backToHome')}</a>
          </footer>
        </article>
      </div>
    {:else if match}
      <ReadOnlyScoreBoard {match} />
      <div class="auto-refresh-info">
        <ion-icon name="sync-outline"></ion-icon>
        <span>{$_('scoreBoard.autoRefresh')}</span>
      </div>
    {/if}
  </div>
</div>

<style>
  .share-view {
    min-height: 100vh;
    padding: 2rem 0;
  }

  .container {
    max-width: 800px;
    margin: 0 auto;
    padding: 0 1rem;
  }

  .loading-container,
  .error-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 50vh;
  }

  .error-message {
    text-align: center;
    color: var(--pico-del-color);
  }

  .auto-refresh-info {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    margin-top: 2rem;
    padding: 0.75rem;
    background-color: var(--pico-secondary-background);
    border-radius: var(--pico-border-radius);
    color: var(--pico-muted-color);
    font-size: 0.875rem;
  }

  .auto-refresh-info ion-icon {
    font-size: 1rem;
  }
</style>
